import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  MenuItem,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import BlockIcon from '@mui/icons-material/Block';
import PlaceIcon from '@mui/icons-material/Place';
import ClockMap from './Maps/ClockMap';
import { fmtTime } from '../utils/timesheetUtils';

export default function DayDetailDialog({
  detail,
  setDetail,
  fetchData,
  filterStart,
  filterEnd,
  reqId,
  selectedDepartment,
  selectedTeam,
}) {
  const [editRecord, setEditRecord] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    timestamp: '',
    lat: '',
    lon: '',
    event_type: 'clock_in',
  });
  const [isSaving, setIsSaving] = useState(false);
  // approve/reject out-of-zone clock-in
  const [decisionLoading, setDecisionLoading] = useState({ id: null, decision: null });
  const [isAdmin, setIsAdmin] = useState(false);
  const [canModerateOutOfZone, setCanModerateOutOfZone] = useState(false);
  const [roleError, setRoleError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  // Check user role via API
  useEffect(() => {
    const checkUserRole = async () => {
      try {
        const authToken = localStorage.getItem('auth_token') || '';
        if (!authToken) {
          setRoleError('No auth_token found');
          setIsAdmin(false);
          return;
        }

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/admin/user/role?auth_token=${authToken}`,
          { method: 'GET', headers: { 'Content-Type': 'application/json' } }
        );
        const data = await response.json();

        if (data.status === 'success' && data.role) {
          const role = data.role.toLowerCase();
          localStorage.setItem('user_role', role);
          // Edit/Add record is restricted to admin/ceo/hr
          setIsAdmin(['admin', 'ceo', 'hr'].includes(role));
          // Out-of-zone moderation can be broader
          setCanModerateOutOfZone(['admin', 'ceo', 'hr', 'hod', 'manager', 'supervisor'].includes(role));
        } else {
          setRoleError(data.message || 'Failed to fetch user role');
          setIsAdmin(false);
          setCanModerateOutOfZone(false);
        }
      } catch (error) {
        setRoleError('Error fetching user role');
        setIsAdmin(false);
        setCanModerateOutOfZone(false);
      }
    };

    if (detail) checkUserRole();
  }, [detail]);

  // Log records for debugging
  useEffect(() => {
    if (detail?.day?.records?.length) {
      const recordsWithoutId = detail.day.records.filter(r => !r.id);
      if (recordsWithoutId.length > 0) {
        // оставил лёгкий лог — можно убрать, если совсем без дебага
        console.log('Records without ID:', recordsWithoutId);
      }
    }
  }, [detail]);

  const initializeNewRecord = useCallback(() => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const timestampStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;

    setFormData({
      timestamp: timestampStr,
      lat: '',
      lon: '',
      event_type: 'clock_in',
    });
    setIsCreating(true);
    setEditRecord(null);
  }, []);

  const handleEdit = useCallback((record) => {
    let timestampStr = '';
    if (record.timestamp) {
      const date = new Date(record.timestamp * 1000);
      const pad = (n) => String(n).padStart(2, '0');
      timestampStr = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    }

    setEditRecord(record);
    setFormData({
      timestamp: timestampStr,
      lat: record.lat != null ? record.lat.toString() : '',
      lon: record.lon != null ? record.lon.toString() : '',
      event_type: record.event_type || 'clock_in',
    });
    setIsCreating(false);
  }, []);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const validateForm = useCallback(() => {
    if (!/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(formData.timestamp)) {
      return 'Invalid timestamp format (expected: YYYY-MM-DD HH:MM:SS)';
    }
    if (formData.lat && isNaN(parseFloat(formData.lat))) return 'Latitude must be a valid number';
    if (formData.lon && isNaN(parseFloat(formData.lon))) return 'Longitude must be a valid number';
    if (!['clock_in', 'clock_out'].includes(formData.event_type)) return 'Event type must be "clock_in" or "clock_out"';
    return null;
  }, [formData]);

  const handleSave = useCallback(async () => {
    if (isSaving) return;

    const error = validateForm();
    if (error) {
      setSnackbar({ open: true, message: error, severity: 'error' });
      return;
    }
    if (!editRecord?.id) {
      setSnackbar({ open: true, message: 'Cannot save record without ID.', severity: 'error' });
      return;
    }

    setIsSaving(true);
    try {
      const authToken = localStorage.getItem('auth_token') || '';
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/admin/records/${editRecord.id}?auth_token=${authToken}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            timestamp: formData.timestamp,
            lat: formData.lat ? parseFloat(formData.lat) : null,
            lon: formData.lon ? parseFloat(formData.lon) : null,
            event_type: formData.event_type,
          }),
        }
      );

      const result = await response.json();
      if (result.status === 'success') {
        setDetail((prev) => ({
          ...prev,
          day: {
            ...prev.day,
            records: prev.day.records.map((r) =>
              r.id === editRecord.id
                ? {
                    ...r,
                    timestamp: new Date(formData.timestamp).getTime() / 1000,
                    lat: formData.lat ? parseFloat(formData.lat) : null,
                    lon: formData.lon ? parseFloat(formData.lon) : null,
                    event_type: formData.event_type,
                  }
                : r
            ),
          },
        }));
        if (fetchData) {
          fetchData(filterStart, filterEnd, reqId, selectedDepartment?.id, selectedTeam?.id);
        }
        setEditRecord(null);
        setSnackbar({ open: true, message: 'Record updated successfully', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: result.message || 'Failed to update record', severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Error updating record: ' + error.message, severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  }, [editRecord, formData, setDetail, fetchData, filterStart, filterEnd, reqId, selectedDepartment, selectedTeam, validateForm, isSaving]);

  const handleCreate = useCallback(async () => {
    if (isSaving) return;

    const error = validateForm();
    if (error) {
      setSnackbar({ open: true, message: error, severity: 'error' });
      return;
    }

    setIsSaving(true);
    try {
      const authToken = localStorage.getItem('auth_token') || '';
      const employeeId = detail?.employee?.id;
      const dayId = detail?.day?.id;

      if (!employeeId) throw new Error('Employee ID is missing');

      const queryParams = new URLSearchParams({
        auth_token: authToken,
        employee_id: String(employeeId),
        ...(dayId ? { day_id: String(dayId) } : {}),
      });

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/admin/records?${queryParams}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            timestamp: formData.timestamp,
            lat: formData.lat ? parseFloat(formData.lat) : null,
            lon: formData.lon ? parseFloat(formData.lon) : null,
            event_type: formData.event_type,
          }),
        }
      );

      const result = await response.json();
      if (result.status === 'success') {
        const newRecord = {
          id: result.record_id,
          timestamp: new Date(formData.timestamp).getTime() / 1000,
          lat: formData.lat ? parseFloat(formData.lat) : null,
          lon: formData.lon ? parseFloat(formData.lon) : null,
          event_type: formData.event_type,
          out_of_zone: false,
          manager_confirmed: true, // бэкенд нормализует при следующей выборке
        };

        setDetail((prev) => ({
          ...prev,
          day: { ...prev.day, records: [...(prev.day.records || []), newRecord] },
        }));
        if (fetchData) {
          fetchData(filterStart, filterEnd, reqId, selectedDepartment?.id, selectedTeam?.id);
        }
        setIsCreating(false);
        setFormData({ timestamp: '', lat: '', lon: '', event_type: 'clock_in' });
        setSnackbar({ open: true, message: 'Record created successfully', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: result.message || 'Failed to create record', severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Error creating record: ' + error.message, severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  }, [formData, detail, setDetail, fetchData, filterStart, filterEnd, reqId, selectedDepartment, selectedTeam, validateForm, isSaving]);

  const handleCancel = useCallback(() => {
    setEditRecord(null);
    setIsCreating(false);
    setFormData({ timestamp: '', lat: '', lon: '', event_type: 'clock_in' });
  }, []);

  // ✅ без зависимости от snackbar — нет ворнинга
  const handleSnackbarClose = useCallback(() => {
    setSnackbar((s) => ({ ...s, open: false }));
  }, []);

  // Approve/Reject out-of-zone clock-in for web admin
  const handleOutOfZoneDecision = useCallback(
    async (record, decision) => {
      if (!record?.id) {
        setSnackbar({ open: true, message: 'Cannot change approval status: record has no ID', severity: 'error' });
        return;
      }
      if (decisionLoading.id === record.id) return;

      const authToken = localStorage.getItem('auth_token') || '';
      if (!authToken) {
        setSnackbar({ open: true, message: 'No auth_token found', severity: 'error' });
        return;
      }

      setDecisionLoading({ id: record.id, decision });
      try {
        const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const payload = { auth_token: authToken, record_id: record.id, decision };

        // Primary (recommended): /admin/out-of-zone/decision  -> external becomes /api/admin/out-of-zone/decision
        // Fallback (legacy/double-api): /api/admin/out-of-zone/decision -> external becomes /api/api/admin/out-of-zone/decision
        const urlsToTry = [
          `${API}/admin/out-of-zone/decision`,
          `${API}/api/admin/out-of-zone/decision`,
        ];

        let lastJson = null;
        let lastStatus = 0;
        let ok = false;

        for (const url of urlsToTry) {
          const resp = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
          lastStatus = resp.status;
          const j = await resp.json().catch(() => null);
          lastJson = j;

          if (resp.ok) {
            ok = true;
            break;
          }

          // try next only on 404
          if (resp.status !== 404) break;
        }

        if (!ok) {
          const msg =
            (lastJson && (lastJson.message || lastJson.error)) ||
            (lastStatus === 403
              ? 'Access denied'
              : `Failed to ${decision} (HTTP ${lastStatus || 'error'})`);
          setSnackbar({ open: true, message: msg, severity: 'error' });
          return;
        }

        // Optimistic UI update + hard refresh
        setDetail((prev) => {
          if (!prev?.day?.records) return prev;
          return {
            ...prev,
            day: {
              ...prev.day,
              records: prev.day.records.map((r) =>
                r.id === record.id
                  ? {
                      ...r,
                      manager_confirmed: decision === 'approve',
                    }
                  : r
              ),
            },
          };
        });

        if (fetchData) {
          fetchData(filterStart, filterEnd, reqId, selectedDepartment?.id, selectedTeam?.id);
        }

        setSnackbar({
          open: true,
          message: decision === 'approve' ? 'Approved successfully' : 'Declined successfully',
          severity: 'success',
        });
      } catch (e) {
        setSnackbar({ open: true, message: `Error: ${e?.message || e}`, severity: 'error' });
      } finally {
        setDecisionLoading({ id: null, decision: null });
      }
    },
    [decisionLoading, fetchData, filterStart, filterEnd, reqId, selectedDepartment, selectedTeam, setDetail]
  );

  // помощник для бейджа статуса out-of-zone
  const OutOfZoneChip = ({ record }) => {
    if (record.event_type !== 'clock_in' || !record.out_of_zone) return null;

    // Если менеджер подтвердил — Approved, иначе Not approved/Pending
    const approved = Boolean(record.manager_confirmed);
    const label = approved ? 'Out of zone — Approved' : 'Out of zone — Not approved';
    const icon = approved ? <CheckCircleIcon /> : <HourglassBottomIcon />;

    return (
      <Tooltip title={approved ? 'Manager approved this out-of-zone clock-in' : 'Awaiting approval or rejected'}>
        <Chip
          size="small"
          icon={icon}
          label={label}
          sx={{ ml: 1 }}
          color={approved ? 'success' : 'error'}
          variant={approved ? 'filled' : 'outlined'}
        />
      </Tooltip>
    );
  };

  return (
    <Dialog
      open={Boolean(detail)}
      onClose={() => setDetail(null)}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { width: '980px', maxWidth: '95vw' } }}
    >
      <DialogTitle>
        {detail && `${detail.employee.first_name} ${detail.employee.last_name} — ${detail.day?.date}`}
      </DialogTitle>
      <DialogContent dividers>
        {roleError && (
          <Typography color="error" variant="body2" sx={{ mb: 1 }}>
            {roleError}
          </Typography>
        )}

        {detail?.day?.records?.length > 0 ? (
          detail.day.records.map((r, index) => (
            <Box key={r.id ?? `record-${r.timestamp}-${index}`} sx={{ display: 'flex', alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
              {editRecord === r ? (
                <>
                  <TextField
                    name="timestamp"
                    label="Timestamp"
                    value={formData.timestamp}
                    onChange={handleInputChange}
                    size="small"
                    sx={{ mr: 1, width: 160 }}
                    disabled={isSaving}
                    error={!/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(formData.timestamp)}
                    helperText={
                      !/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(formData.timestamp)
                        ? 'Format: YYYY-MM-DD HH:MM:SS'
                        : ''
                    }
                  />
                  <TextField
                    name="lat"
                    label="Latitude"
                    value={formData.lat}
                    onChange={handleInputChange}
                    size="small"
                    sx={{ mr: 1, width: 100 }}
                    disabled={isSaving}
                    error={formData.lat && isNaN(parseFloat(formData.lat))}
                  />
                  <TextField
                    name="lon"
                    label="Longitude"
                    value={formData.lon}
                    onChange={handleInputChange}
                    size="small"
                    sx={{ mr: 1, width: 100 }}
                    disabled={isSaving}
                    error={formData.lon && isNaN(parseFloat(formData.lon))}
                  />
                  <TextField
                    name="event_type"
                    select
                    label="Event Type"
                    value={formData.event_type}
                    onChange={handleInputChange}
                    size="small"
                    sx={{ mr: 1, width: 120 }}
                    disabled={isSaving}
                  >
                    <MenuItem value="clock_in">Clock In</MenuItem>
                    <MenuItem value="clock_out">Clock Out</MenuItem>
                  </TextField>
                  <IconButton onClick={handleSave} size="small" disabled={isSaving}>
                    {isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
                  </IconButton>
                  <IconButton onClick={handleCancel} size="small" disabled={isSaving}>
                    <CancelIcon />
                  </IconButton>
                </>
              ) : (
                <>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                    {r.event_type === 'clock_in' && r.out_of_zone && <PlaceIcon fontSize="small" color="error" sx={{ mr: 0.5 }} />}
                    {fmtTime(r.timestamp)} — {r.event_type} (Lat: {r.lat ?? 'N/A'}, Lon: {r.lon ?? 'N/A'})
                  </Typography>
                  {/* Статус out-of-zone */}
                  <OutOfZoneChip record={r} />

                  {/* Approve / Decline buttons for out-of-zone clock-in (web admin) */}
                  {canModerateOutOfZone &&
                    r.event_type === 'clock_in' &&
                    r.out_of_zone &&
                    !Boolean(r.manager_confirmed) &&
                    r.id && (
                      <Box sx={{ display: 'flex', alignItems: 'center', ml: 1, gap: 1 }}>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={<CheckCircleIcon />}
                          disabled={
                            isSaving ||
                            (decisionLoading.id === r.id && decisionLoading.decision === 'approve')
                          }
                          onClick={() => handleOutOfZoneDecision(r, 'approve')}
                        >
                          Approve
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<BlockIcon />}
                          disabled={
                            isSaving ||
                            (decisionLoading.id === r.id && decisionLoading.decision === 'reject')
                          }
                          onClick={() => handleOutOfZoneDecision(r, 'reject')}
                        >
                          Decline
                        </Button>
                      </Box>
                    )}

                  {isAdmin && (
                    <IconButton onClick={() => handleEdit(r)} size="small" disabled={isSaving} sx={{ ml: 0.5 }}>
                      <EditIcon />
                    </IconButton>
                  )}
                </>
              )}
            </Box>
          ))
        ) : (
          <Typography variant="body2">No records available</Typography>
        )}

        {isCreating && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: 2, flexWrap: 'wrap' }}>
            <TextField
              name="timestamp"
              label="Timestamp"
              value={formData.timestamp}
              onChange={handleInputChange}
              size="small"
              sx={{ mr: 1, width: 160 }}
              disabled={isSaving}
              error={!/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(formData.timestamp)}
              helperText={
                !/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(formData.timestamp)
                  ? 'Format: YYYY-MM-DD HH:MM:SS'
                  : ''
              }
            />
            <TextField
              name="lat"
              label="Latitude"
              value={formData.lat}
              onChange={handleInputChange}
              size="small"
              sx={{ mr: 1, width: 100 }}
              disabled={isSaving}
              error={formData.lat && isNaN(parseFloat(formData.lat))}
            />
            <TextField
              name="lon"
              label="Longitude"
              value={formData.lon}
              onChange={handleInputChange}
              size="small"
              sx={{ mr: 1, width: 100 }}
              disabled={isSaving}
              error={formData.lon && isNaN(parseFloat(formData.lon))}
            />
            <TextField
              name="event_type"
              select
              label="Event Type"
              value={formData.event_type}
              onChange={handleInputChange}
              size="small"
              sx={{ mr: 1, width: 120 }}
              disabled={isSaving}
            >
              <MenuItem value="clock_in">Clock In</MenuItem>
              <MenuItem value="clock_out">Clock Out</MenuItem>
            </TextField>
            <IconButton onClick={handleCreate} size="small" disabled={isSaving}>
              {isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
            </IconButton>
            <IconButton onClick={handleCancel} size="small" disabled={isSaving}>
              <CancelIcon />
            </IconButton>
          </Box>
        )}

        <Box sx={{ mt: 2 }}>
          <ClockMap records={detail?.day?.records || []} zones={detail?.zones || []} />
        </Box>
      </DialogContent>

      <DialogActions>
        {isAdmin && !isCreating && (
          <Button onClick={initializeNewRecord} disabled={isSaving} startIcon={<AddIcon />}>
            Add Record
          </Button>
        )}
        <Button onClick={() => setDetail(null)} disabled={isSaving}>
          Close
        </Button>
      </DialogActions>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert severity={snackbar.severity} onClose={handleSnackbarClose}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
}
